import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCMS } from './CMSContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import {
  BarChart3,
  TrendingUp,
  Mail,
  Search,
  Settings,
  Play,
  RefreshCw,
  Download,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
  Send,
  Zap,
  Users,
  Filter,
  MapPin,
  Hotel,
  Eye,
  Plus,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MarketingDashboard = () => {
  const { token } = useCMS();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [scraperConfig, setScraperConfig] = useState(null);
  const [priceTrends, setPriceTrends] = useState([]);
  const [automationSettings, setAutomationSettings] = useState(null);
  const [scraperLogs, setScraperLogs] = useState([]);
  const [previewHotels, setPreviewHotels] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [runningAction, setRunningAction] = useState(null);

  // Enhanced Scraping State
  const [manualScrape, setManualScrape] = useState({
    hotel_name: '',
    destination: '',
    check_in: '',
    check_out: ''
  });
  const [destinations, setDestinations] = useState([]);
  const [scrapeResults, setScrapeResults] = useState([]);
  const [scrapeFilters, setScrapeFilters] = useState({
    destination: '',
    date_from: '',
    date_to: ''
  });

  // Custom Campaign State
  const [showCampaignBuilder, setShowCampaignBuilder] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    subject: '',
    template_id: 'price_alert',
    filters: {
      destination: '',
      search_date_from: '',
      search_date_to: '',
      has_email: true
    },
    test_mode: false,
    test_email: ''
  });
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, configRes, trendsRes, automationRes] = await Promise.all([
        axios.get(`${API}/api/cms/marketing/dashboard`, { headers }),
        axios.get(`${API}/api/cms/marketing/scraper/config`, { headers }),
        axios.get(`${API}/api/cms/marketing/price-trends?days=7`, { headers }),
        axios.get(`${API}/api/cms/marketing/automation`, { headers }),
      ]);

      setDashboardStats(statsRes.data);
      setScraperConfig(configRes.data);
      setPriceTrends(trendsRes.data.trends || []);
      setAutomationSettings(automationRes.data);
    } catch (error) {
      console.error('Failed to load marketing dashboard:', error);
      toast.error('Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadScraperLogs = async () => {
    try {
      const res = await axios.get(`${API}/api/cms/marketing/scraper/logs?limit=20`, { headers });
      setScraperLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to load scraper logs:', error);
    }
  };

  const loadPreviewHotels = async () => {
    try {
      const res = await axios.get(`${API}/api/cms/marketing/scraper/preview?limit=20`, { headers });
      setPreviewHotels(res.data.hotels || []);
    } catch (error) {
      console.error('Failed to load preview hotels:', error);
    }
  };

  const loadDestinations = async () => {
    try {
      const res = await axios.get(`${API}/api/cms/marketing/destinations`, { headers });
      setDestinations(res.data.destinations || []);
    } catch (error) {
      console.error('Failed to load destinations:', error);
    }
  };

  const loadScrapeResults = async () => {
    try {
      const params = new URLSearchParams();
      if (scrapeFilters.destination) params.append('destination', scrapeFilters.destination);
      if (scrapeFilters.date_from) params.append('date_from', scrapeFilters.date_from);
      if (scrapeFilters.date_to) params.append('date_to', scrapeFilters.date_to);
      
      const res = await axios.get(`${API}/api/cms/marketing/scrape-results?${params.toString()}`, { headers });
      setScrapeResults(res.data.results || []);
    } catch (error) {
      console.error('Failed to load scrape results:', error);
    }
  };

  const loadCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/api/cms/marketing/campaigns`, { headers });
      setCampaigns(res.data.campaigns || []);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const loadEmailTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/cms/marketing/email-templates`, { headers });
      setEmailTemplates(res.data.templates || []);
    } catch (error) {
      console.error('Failed to load email templates:', error);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (activeTab === 'scraping') {
      loadScraperLogs();
      loadPreviewHotels();
      loadDestinations();
      loadScrapeResults();
    }
    if (activeTab === 'campaigns') {
      loadCampaigns();
      loadEmailTemplates();
      loadDestinations();
    }
  }, [activeTab]);

  const saveScraperConfig = async () => {
    try {
      setSavingConfig(true);
      await axios.post(`${API}/api/cms/marketing/scraper/config`, scraperConfig, { headers });
      toast.success('Configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const runScraperNow = async () => {
    try {
      setRunningAction('scraper');
      await axios.post(`${API}/api/cms/marketing/scraper/run`, {}, { headers });
      toast.success('Scraper started in background');
      setTimeout(() => {
        loadDashboard();
        loadScraperLogs();
      }, 2000);
    } catch (error) {
      toast.error('Failed to start scraper');
    } finally {
      setRunningAction(null);
    }
  };

  const runManualScrape = async () => {
    if (!manualScrape.hotel_name || !manualScrape.destination || !manualScrape.check_in || !manualScrape.check_out) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      setRunningAction('manual_scrape');
      await axios.post(`${API}/api/cms/marketing/scraper/manual`, manualScrape, { headers });
      toast.success(`Scraping started for ${manualScrape.hotel_name}`);
      setTimeout(() => {
        loadScrapeResults();
      }, 5000);
    } catch (error) {
      toast.error('Failed to start manual scrape');
    } finally {
      setRunningAction(null);
    }
  };

  const exportData = async (dateFilter) => {
    try {
      window.open(`${API}/api/cms/marketing/export/scrapes?date_filter=${dateFilter}`, '_blank');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const toggleAutomation = async (setting, enabled) => {
    try {
      await axios.post(`${API}/api/cms/marketing/automation/toggle`, { setting, enabled }, { headers });
      toast.success(`${setting.replace('_', ' ')} ${enabled ? 'enabled' : 'disabled'}`);
      
      // Update local state
      setAutomationSettings(prev => {
        const newSettings = { ...prev };
        if (setting === 'daily_scraper') {
          newSettings.daily_scraper = { ...prev.daily_scraper, enabled };
        } else if (setting === 'search_followup') {
          newSettings.search_followup = { ...prev.search_followup, enabled };
        } else if (setting === 'price_alert') {
          newSettings.price_alert = { ...prev.price_alert, auto_send: enabled };
        } else if (setting === 'after_sale') {
          newSettings.after_sale = { ...prev.after_sale, auto_send: enabled };
        }
        return newSettings;
      });
    } catch (error) {
      toast.error(`Failed to toggle ${setting}`);
    }
  };

  const previewRecipients = async () => {
    try {
      setLoadingRecipients(true);
      const res = await axios.post(`${API}/api/cms/marketing/campaigns/preview-recipients`, campaignForm.filters, { headers });
      setRecipientPreview(res.data);
    } catch (error) {
      toast.error('Failed to preview recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const createCampaign = async (testMode = false) => {
    if (!campaignForm.name || !campaignForm.subject) {
      toast.error('Please fill in campaign name and subject');
      return;
    }
    
    if (testMode && !campaignForm.test_email) {
      toast.error('Please enter a test email address');
      return;
    }
    
    try {
      setRunningAction(testMode ? 'test_campaign' : 'send_campaign');
      const payload = {
        ...campaignForm,
        test_mode: testMode,
        test_email: testMode ? campaignForm.test_email : null
      };
      
      const res = await axios.post(`${API}/api/cms/marketing/campaigns/create`, payload, { headers });
      
      if (res.data.success) {
        toast.success(res.data.message);
        if (!testMode) {
          setShowCampaignBuilder(false);
          loadCampaigns();
        }
      } else {
        toast.error(res.data.message || 'Campaign creation failed');
      }
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setRunningAction(null);
    }
  };

  // Set default dates for manual scrape
  useEffect(() => {
    if (!manualScrape.check_in) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 7);
      const checkOut = new Date(tomorrow);
      checkOut.setDate(checkOut.getDate() + 2);
      
      setManualScrape(prev => ({
        ...prev,
        check_in: tomorrow.toISOString().split('T')[0],
        check_out: checkOut.toISOString().split('T')[0]
      }));
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2">Loading marketing data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="scraping" className="flex items-center gap-2">
            <Search className="w-4 h-4" />
            Price Scraping
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email Campaigns
          </TabsTrigger>
          <TabsTrigger value="automation" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Automation
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Searches Today</CardDescription>
                <CardTitle className="text-3xl">{dashboardStats?.searches?.today || 0}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {dashboardStats?.searches?.week || 0} this week
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Emails Captured</CardDescription>
                <CardTitle className="text-3xl">{dashboardStats?.emails?.captured_week || 0}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {dashboardStats?.emails?.followups_sent_week || 0} follow-ups sent
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Scrapes Today</CardDescription>
                <CardTitle className="text-3xl">{dashboardStats?.scrapes?.today || 0}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {dashboardStats?.scrapes?.week || 0} this week
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Avg Savings</CardDescription>
                <CardTitle className="text-3xl text-green-600">
                  {dashboardStats?.avg_savings_percent || 0}%
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">vs OTA platforms</p>
              </CardContent>
            </Card>
          </div>

          {/* Price Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Price Trends (7 Days)
              </CardTitle>
              <CardDescription>Average scraped prices comparison</CardDescription>
            </CardHeader>
            <CardContent>
              {priceTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={priceTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value) => `€${value}`} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="market_low" 
                      stroke="#10b981" 
                      name="Market Low"
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="booking" 
                      stroke="#3b82f6" 
                      name="Booking.com"
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="market_price" 
                      stroke="#6b7280" 
                      name="Market Price"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <p>No trend data available yet. Run some scrapes to see trends.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Destinations */}
          <Card>
            <CardHeader>
              <CardTitle>Top Searched Destinations</CardTitle>
              <CardDescription>Most popular destinations this week</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboardStats?.top_destinations?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={dashboardStats.top_destinations} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="destination" type="category" width={100} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" name="Searches" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No search data available yet
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Price Scraping Tab - ENHANCED */}
        <TabsContent value="scraping" className="space-y-6">
          {/* Manual Scraping Card - NEW */}
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hotel className="w-5 h-5 text-blue-500" />
                Manual Scraping
                <Badge variant="outline" className="ml-2">Enhanced</Badge>
              </CardTitle>
              <CardDescription>Scrape a specific hotel with custom dates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Hotel Name</Label>
                  <Input
                    placeholder="e.g., Hilton Amsterdam"
                    value={manualScrape.hotel_name}
                    onChange={(e) => setManualScrape(prev => ({ ...prev, hotel_name: e.target.value }))}
                    data-testid="manual-scrape-hotel-name"
                  />
                </div>
                <div>
                  <Label>Destination</Label>
                  <Select
                    value={manualScrape.destination}
                    onValueChange={(value) => setManualScrape(prev => ({ ...prev, destination: value }))}
                  >
                    <SelectTrigger data-testid="manual-scrape-destination">
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinations.map((dest) => (
                        <SelectItem key={dest.name} value={dest.name}>
                          {dest.name} ({dest.search_count} searches)
                        </SelectItem>
                      ))}
                      {destinations.length === 0 && (
                        <SelectItem value="custom" disabled>
                          No destinations yet - type manually below
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {destinations.length === 0 && (
                    <Input
                      className="mt-2"
                      placeholder="Type destination manually"
                      value={manualScrape.destination}
                      onChange={(e) => setManualScrape(prev => ({ ...prev, destination: e.target.value }))}
                    />
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Check-in Date</Label>
                  <Input
                    type="date"
                    value={manualScrape.check_in}
                    onChange={(e) => setManualScrape(prev => ({ ...prev, check_in: e.target.value }))}
                    data-testid="manual-scrape-checkin"
                  />
                </div>
                <div>
                  <Label>Check-out Date</Label>
                  <Input
                    type="date"
                    value={manualScrape.check_out}
                    onChange={(e) => setManualScrape(prev => ({ ...prev, check_out: e.target.value }))}
                    data-testid="manual-scrape-checkout"
                  />
                </div>
              </div>

              <Button 
                onClick={runManualScrape}
                disabled={runningAction === 'manual_scrape'}
                data-testid="run-manual-scrape-btn"
              >
                {runningAction === 'manual_scrape' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Start Scraping
              </Button>
            </CardContent>
          </Card>

          {/* Scrape Results with Filters - NEW */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Scrape Results & Price Comparisons
                  </CardTitle>
                  <CardDescription>Combined view: OTA scraper results and user search comparisons</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => exportData('today')}>
                    <Download className="w-4 h-4 mr-2" />
                    Export Today
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => exportData('week')}>
                    <Download className="w-4 h-4 mr-2" />
                    Export Week
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 bg-secondary/30 rounded-lg">
                <div>
                  <Label>Destination</Label>
                  <Select
                    value={scrapeFilters.destination}
                    onValueChange={(value) => setScrapeFilters(prev => ({ ...prev, destination: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All destinations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All destinations</SelectItem>
                      {destinations.map((dest) => (
                        <SelectItem key={dest.name} value={dest.name}>
                          {dest.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={scrapeFilters.date_from}
                    onChange={(e) => setScrapeFilters(prev => ({ ...prev, date_from: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={scrapeFilters.date_to}
                    onChange={(e) => setScrapeFilters(prev => ({ ...prev, date_to: e.target.value }))}
                  />
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={loadScrapeResults}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Apply Filters
                  </Button>
                </div>
              </div>

              {/* Results Table */}
              {scrapeResults.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Hotel</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead className="text-blue-600">Freestays</TableHead>
                      <TableHead>Booking.com</TableHead>
                      <TableHead>Market Low</TableHead>
                      <TableHead className="text-green-600">Besparing</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scrapeResults.slice(0, 20).map((result, idx) => {
                      const freestaysPrice = result.prices?.['Freestays'] || result.freestays_price;
                      const bookingPrice = result.prices?.['Booking.com'];
                      const marketLow = result.prices?.['Market Low'];
                      const comparePrice = bookingPrice || marketLow;
                      const savings = freestaysPrice && comparePrice 
                        ? Math.round(((comparePrice - freestaysPrice) / comparePrice) * 100)
                        : null;
                      
                      return (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{result.hotel_name}</TableCell>
                          <TableCell>{result.destination}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {result.check_in} - {result.check_out}
                          </TableCell>
                          <TableCell className="text-blue-600 font-semibold">
                            {freestaysPrice ? `€${freestaysPrice}` : '-'}
                          </TableCell>
                          <TableCell>
                            {bookingPrice ? `€${bookingPrice}` : '-'}
                          </TableCell>
                          <TableCell>
                            {marketLow ? `€${marketLow}` : '-'}
                          </TableCell>
                          <TableCell>
                            {savings !== null ? (
                              <Badge variant={savings > 0 ? "default" : "secondary"} 
                                     className={savings > 10 ? "bg-green-600" : savings > 0 ? "bg-green-500" : ""}>
                                {savings > 0 ? `${savings}% goedkoper` : savings === 0 ? 'Gelijk' : `${Math.abs(savings)}% duurder`}
                              </Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={result.source === 'price_comparison' ? 'border-blue-400 text-blue-600' : 'border-amber-400 text-amber-600'}>
                              {result.source === 'price_comparison' ? 'Search' : 'Scraper'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={result.success ? "default" : "secondary"}>
                              {result.success ? "Success" : "No prices"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No scrape results found. Run a manual or automatic scrape to see results.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Scraper Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Daily Scraper Configuration
              </CardTitle>
              <CardDescription>Configure the automated daily price scraper</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
                <div className="flex items-center gap-4">
                  <Switch
                    checked={scraperConfig?.enabled || false}
                    onCheckedChange={(checked) => 
                      setScraperConfig(prev => ({ ...prev, enabled: checked }))
                    }
                  />
                  <div>
                    <p className="font-medium">Enable Daily Scraping</p>
                    <p className="text-sm text-muted-foreground">
                      Automatically scrape hotels based on search history
                    </p>
                  </div>
                </div>
                <Badge variant={scraperConfig?.enabled ? "default" : "secondary"}>
                  {scraperConfig?.enabled ? "Active" : "Disabled"}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Max Hotels per Day</Label>
                  <Input
                    type="number"
                    value={scraperConfig?.limits?.max_per_day || 50}
                    onChange={(e) => setScraperConfig(prev => ({
                      ...prev,
                      limits: { ...prev.limits, max_per_day: parseInt(e.target.value) }
                    }))}
                  />
                </div>
                <div>
                  <Label>Cache Duration (days)</Label>
                  <Input
                    type="number"
                    value={scraperConfig?.limits?.cache_days || 7}
                    onChange={(e) => setScraperConfig(prev => ({
                      ...prev,
                      limits: { ...prev.limits, cache_days: parseInt(e.target.value) }
                    }))}
                  />
                </div>
                <div>
                  <Label>Min Search Count</Label>
                  <Input
                    type="number"
                    value={scraperConfig?.limits?.min_search_count || 2}
                    onChange={(e) => setScraperConfig(prev => ({
                      ...prev,
                      limits: { ...prev.limits, min_search_count: parseInt(e.target.value) }
                    }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Priority Mode</Label>
                  <Select
                    value={scraperConfig?.priority || 'smart_mix'}
                    onValueChange={(value) => 
                      setScraperConfig(prev => ({ ...prev, priority: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="most_searched">Most Searched First</SelectItem>
                      <SelectItem value="highest_stars">Highest Stars First</SelectItem>
                      <SelectItem value="smart_mix">Smart Mix (Recommended)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Min Savings for Price Alert (%)</Label>
                  <Input
                    type="number"
                    value={scraperConfig?.min_savings_percent || 15}
                    onChange={(e) => setScraperConfig(prev => ({
                      ...prev,
                      min_savings_percent: parseInt(e.target.value)
                    }))}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={saveScraperConfig} disabled={savingConfig}>
                  {savingConfig ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Save Configuration
                </Button>
                <Button 
                  variant="outline" 
                  onClick={runScraperNow}
                  disabled={runningAction === 'scraper'}
                >
                  {runningAction === 'scraper' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  Run Now
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Scraper Status */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Scraper Status</CardTitle>
                  <CardDescription>Current status and recent activity</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={loadScraperLogs}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="font-medium flex items-center gap-2">
                    {dashboardStats?.scraper?.enabled ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Active
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-gray-500" />
                        Disabled
                      </>
                    )}
                  </p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Last Run</p>
                  <p className="font-medium">
                    {dashboardStats?.scraper?.last_run 
                      ? new Date(dashboardStats.scraper.last_run).toLocaleString()
                      : 'Never'
                    }
                  </p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Last Stats</p>
                  <p className="font-medium">
                    {dashboardStats?.scraper?.last_run_stats 
                      ? `${dashboardStats.scraper.last_run_stats.completed} completed, ${dashboardStats.scraper.last_run_stats.failed} failed`
                      : 'No data'
                    }
                  </p>
                </div>
              </div>

              {/* Recent Logs */}
              <div>
                <h4 className="font-medium mb-2">Recent Jobs</h4>
                {scraperLogs.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job ID</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Stats</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scraperLogs.slice(0, 5).map((log) => (
                        <TableRow key={log.job_id}>
                          <TableCell className="font-mono text-xs">{log.job_id}</TableCell>
                          <TableCell>
                            {log.started_at 
                              ? new Date(log.started_at).toLocaleString()
                              : '-'
                            }
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              log.status === 'completed' ? 'default' :
                              log.status === 'running' ? 'secondary' : 'destructive'
                            }>
                              {log.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {log.stats 
                              ? `${log.stats.hotels_completed || 0}/${log.stats.hotels_queued || 0}`
                              : '-'
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    No scraper logs yet
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Preview Hotels */}
          <Card>
            <CardHeader>
              <CardTitle>Hotels Queue Preview</CardTitle>
              <CardDescription>Hotels that would be scraped in the next automatic run</CardDescription>
            </CardHeader>
            <CardContent>
              {previewHotels.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Hotel</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Stars</TableHead>
                      <TableHead>Search Count</TableHead>
                      <TableHead>Priority Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewHotels.map((hotel, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{hotel.hotel_name}</TableCell>
                        <TableCell>{hotel.destination}</TableCell>
                        <TableCell>{'★'.repeat(hotel.star_rating || 3)}</TableCell>
                        <TableCell>{hotel.search_count}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{hotel.priority_score?.toFixed(0) || '-'}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 space-y-2">
                  <p className="text-muted-foreground">
                    No hotels in queue.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Hotels appear here based on search history from the last 7 days.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Campaigns Tab - ENHANCED */}
        <TabsContent value="campaigns" className="space-y-6">
          {/* Campaign Builder Button */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Email Campaigns</h3>
              <p className="text-sm text-muted-foreground">
                Manage automated and custom email campaigns
              </p>
            </div>
            <Button onClick={() => setShowCampaignBuilder(true)} data-testid="create-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Custom Campaign
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Search Follow-up */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-500" />
                  Search Follow-up
                </CardTitle>
                <CardDescription>
                  Automated emails to visitors 24-48h after search
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span>Status</span>
                  <Badge variant={automationSettings?.search_followup?.enabled ? "default" : "secondary"}>
                    {automationSettings?.search_followup?.enabled ? "Active" : "Disabled"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>Pending</span>
                  <span className="font-medium">{automationSettings?.search_followup?.pending || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Total Sent</span>
                  <span className="font-medium">{automationSettings?.search_followup?.sent_total || 0}</span>
                </div>
                <Button variant="outline" className="w-full" asChild>
                  <a href="/admin" target="_blank">Configure in Admin</a>
                </Button>
              </CardContent>
            </Card>

            {/* After Sale */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-purple-500" />
                  After Sale
                </CardTitle>
                <CardDescription>
                  Follow-up for failed/expired payments
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span>Auto-send</span>
                  <Badge variant={automationSettings?.after_sale?.auto_send ? "default" : "secondary"}>
                    {automationSettings?.after_sale?.auto_send ? "On" : "Off"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Emails sent when payments fail or expire. Configure templates in Admin panel.
                </p>
                <Button variant="outline" className="w-full" asChild>
                  <a href="/admin" target="_blank">Configure in Admin</a>
                </Button>
              </CardContent>
            </Card>

            {/* Price Alert */}
            <Card className="border-green-200 dark:border-green-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Price Alert
                  <Badge variant="outline" className="ml-2">NEW</Badge>
                </CardTitle>
                <CardDescription>
                  Emails with REAL scraped OTA price comparisons
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span>Auto-send</span>
                  <Badge variant={automationSettings?.price_alert?.auto_send ? "default" : "secondary"}>
                    {automationSettings?.price_alert?.auto_send ? "On" : "Off"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>Min Savings</span>
                  <span className="font-medium">{automationSettings?.price_alert?.min_savings_percent || 15}%</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Sends emails with actual market prices from daily scraping.
                </p>
                <Button variant="outline" className="w-full" asChild>
                  <a href="/admin?tab=email-templates" target="_blank">Edit Template</a>
                </Button>
              </CardContent>
            </Card>

            {/* Custom Campaigns Summary */}
            <Card className="border-orange-200 dark:border-orange-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-orange-500" />
                  Custom Campaigns
                  <Badge className="ml-2 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">Active</Badge>
                </CardTitle>
                <CardDescription>
                  Manual bulk email campaigns
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span>Total Campaigns</span>
                  <span className="font-medium">{campaigns.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Last Sent</span>
                  <span className="font-medium">
                    {campaigns.find(c => c.status === 'completed')?.completed_at 
                      ? new Date(campaigns.find(c => c.status === 'completed').completed_at).toLocaleDateString()
                      : 'Never'
                    }
                  </span>
                </div>
                <Button className="w-full" onClick={() => setShowCampaignBuilder(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Campaign
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Previous Campaigns List */}
          {campaigns.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Campaign History</CardTitle>
                <CardDescription>Recent custom campaigns</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.campaign_id}>
                        <TableCell className="font-medium">{campaign.name}</TableCell>
                        <TableCell>
                          <Badge variant={
                            campaign.status === 'completed' ? 'default' :
                            campaign.status === 'pending' ? 'secondary' :
                            campaign.status === 'failed' ? 'destructive' : 'outline'
                          }>
                            {campaign.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{campaign.recipient_count || 0}</TableCell>
                        <TableCell>{campaign.sent_count || 0}</TableCell>
                        <TableCell>
                          {campaign.created_at 
                            ? new Date(campaign.created_at).toLocaleDateString()
                            : '-'
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Automation Tab */}
        <TabsContent value="automation" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Marketing Automation
              </CardTitle>
              <CardDescription>Control all automated marketing processes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Daily Scraper */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={automationSettings?.daily_scraper?.enabled || false}
                      onCheckedChange={(checked) => toggleAutomation('daily_scraper', checked)}
                      data-testid="toggle-daily-scraper"
                    />
                    <div>
                      <p className="font-medium">Daily Price Scraper</p>
                      <p className="text-sm text-muted-foreground">
                        Scrapes hotels based on search history (03:30 UTC)
                      </p>
                    </div>
                  </div>
                  <Badge variant={automationSettings?.daily_scraper?.enabled ? "default" : "secondary"}>
                    {automationSettings?.daily_scraper?.enabled ? "Active" : "Disabled"}
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={runScraperNow}
                    disabled={runningAction === 'scraper'}
                  >
                    {runningAction === 'scraper' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Run Now
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setActiveTab('scraping')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Configure
                  </Button>
                </div>
              </div>

              {/* Search Follow-up */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={automationSettings?.search_followup?.enabled || false}
                      onCheckedChange={(checked) => toggleAutomation('search_followup', checked)}
                      data-testid="toggle-search-followup"
                    />
                    <div>
                      <p className="font-medium">Search Follow-up Emails</p>
                      <p className="text-sm text-muted-foreground">
                        Emails sent 24-48h after search to non-registered visitors
                      </p>
                    </div>
                  </div>
                  <Badge variant={automationSettings?.search_followup?.enabled ? "default" : "secondary"}>
                    {automationSettings?.search_followup?.enabled ? "Active" : "Disabled"}
                  </Badge>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">Pending: </span>
                  <span className="font-medium">{automationSettings?.search_followup?.pending || 0}</span>
                  <span className="text-muted-foreground ml-4">Sent: </span>
                  <span className="font-medium">{automationSettings?.search_followup?.sent_total || 0}</span>
                </div>
              </div>

              {/* Price Alert */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={automationSettings?.price_alert?.auto_send || false}
                      onCheckedChange={(checked) => toggleAutomation('price_alert', checked)}
                      data-testid="toggle-price-alert"
                    />
                    <div>
                      <p className="font-medium">Price Alert Emails</p>
                      <p className="text-sm text-muted-foreground">
                        Emails with real OTA prices (requires {automationSettings?.price_alert?.min_savings_percent || 15}%+ savings)
                      </p>
                    </div>
                  </div>
                  <Badge variant={automationSettings?.price_alert?.auto_send ? "default" : "secondary"}>
                    {automationSettings?.price_alert?.auto_send ? "Auto" : "Manual"}
                  </Badge>
                </div>
              </div>

              {/* After Sale */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={automationSettings?.after_sale?.auto_send || false}
                      onCheckedChange={(checked) => toggleAutomation('after_sale', checked)}
                      data-testid="toggle-after-sale"
                    />
                    <div>
                      <p className="font-medium">After Sale Emails</p>
                      <p className="text-sm text-muted-foreground">
                        Automated emails for failed/expired payments
                      </p>
                    </div>
                  </div>
                  <Badge variant={automationSettings?.after_sale?.auto_send ? "default" : "secondary"}>
                    {automationSettings?.after_sale?.auto_send ? "Auto" : "Manual"}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button variant="outline" onClick={runScraperNow} disabled={runningAction === 'scraper'}>
                <Play className="w-4 h-4 mr-2" />
                Run Scraper
              </Button>
              <Button variant="outline" onClick={() => exportData('today')}>
                <Download className="w-4 h-4 mr-2" />
                Export Today
              </Button>
              <Button variant="outline" onClick={() => exportData('week')}>
                <Download className="w-4 h-4 mr-2" />
                Export Week
              </Button>
              <Button variant="outline" onClick={loadDashboard}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh All
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Custom Campaign Builder Dialog */}
      <Dialog open={showCampaignBuilder} onOpenChange={setShowCampaignBuilder}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Create Custom Campaign
            </DialogTitle>
            <DialogDescription>
              Send bulk marketing emails to a targeted audience
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Campaign Details */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Campaign Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Campaign Name</Label>
                  <Input
                    placeholder="e.g., Summer Sale - Amsterdam Hotels"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="campaign-name-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Email Subject</Label>
                  <Input
                    placeholder="e.g., Exclusive deals on hotels in {{destination}}"
                    value={campaignForm.subject}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, subject: e.target.value }))}
                    data-testid="campaign-subject-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Email Template</Label>
                  <Select
                    value={campaignForm.template_id}
                    onValueChange={(value) => setCampaignForm(prev => ({ ...prev, template_id: value }))}
                  >
                    <SelectTrigger data-testid="campaign-template-select">
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTemplates.map((template) => (
                        <SelectItem key={template.template_id} value={template.template_id}>
                          {template.name || template.template_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Audience Filters */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Target Audience
              </h4>
              <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg">
                <div className="col-span-2">
                  <Label>Destination Filter</Label>
                  <Select
                    value={campaignForm.filters.destination}
                    onValueChange={(value) => setCampaignForm(prev => ({ 
                      ...prev, 
                      filters: { ...prev.filters, destination: value === 'all' ? '' : value }
                    }))}
                  >
                    <SelectTrigger data-testid="campaign-destination-filter">
                      <SelectValue placeholder="All destinations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All destinations</SelectItem>
                      {destinations.map((dest) => (
                        <SelectItem key={dest.name} value={dest.name}>
                          {dest.name} ({dest.search_count} searches)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Search Date From</Label>
                  <Input
                    type="date"
                    value={campaignForm.filters.search_date_from}
                    onChange={(e) => setCampaignForm(prev => ({ 
                      ...prev, 
                      filters: { ...prev.filters, search_date_from: e.target.value }
                    }))}
                    data-testid="campaign-date-from"
                  />
                </div>
                <div>
                  <Label>Search Date To</Label>
                  <Input
                    type="date"
                    value={campaignForm.filters.search_date_to}
                    onChange={(e) => setCampaignForm(prev => ({ 
                      ...prev, 
                      filters: { ...prev.filters, search_date_to: e.target.value }
                    }))}
                    data-testid="campaign-date-to"
                  />
                </div>
              </div>

              <Button variant="outline" onClick={previewRecipients} disabled={loadingRecipients}>
                {loadingRecipients ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Preview Recipients
              </Button>

              {/* Recipient Preview */}
              {recipientPreview && (
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-medium flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {recipientPreview.total} Recipients Found
                    </p>
                    {recipientPreview.total > recipientPreview.showing && (
                      <Badge variant="outline">
                        Showing {recipientPreview.showing} of {recipientPreview.total}
                      </Badge>
                    )}
                  </div>
                  {recipientPreview.recipients?.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Destination</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {recipientPreview.recipients.slice(0, 10).map((r, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-sm">{r.email}</TableCell>
                              <TableCell className="text-sm">{r.name || '-'}</TableCell>
                              <TableCell className="text-sm">{r.destination || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No recipients match your filters. Try adjusting the criteria.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Test Email */}
            <div className="space-y-4">
              <h4 className="font-medium">Test Email</h4>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={campaignForm.test_email}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, test_email: e.target.value }))}
                    data-testid="campaign-test-email"
                  />
                </div>
                <Button 
                  variant="outline"
                  onClick={() => createCampaign(true)}
                  disabled={runningAction === 'test_campaign'}
                >
                  {runningAction === 'test_campaign' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Test
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignBuilder(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => createCampaign(false)}
              disabled={runningAction === 'send_campaign' || !recipientPreview?.total}
              data-testid="send-campaign-btn"
            >
              {runningAction === 'send_campaign' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Campaign ({recipientPreview?.total || 0} recipients)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingDashboard;
